import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { PusherContext } from '~/domains/pusher/PusherContext';

import { readPusherMessage, readRequestId } from '../utils/pusherEnvelope';
import { PUSHER_TIMEOUT } from '../constants';
import type { PusherHeaders } from '../types/pusher.types';

import { usePusherChannel } from './usePusherChannel';
import { usePusherEnabledForEndpoint } from './usePusherEnabledForEndpoint';

type UsePusherFetchOptions = {
  endpoint: string;
  enabled?: boolean;
  timeout?: number;
};

type Pending<T> = {
  resolve: (rows: T[]) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

export type Send<T> = (headers?: PusherHeaders, signal?: AbortSignal) => Promise<{ data: T[] }>;

type FetchRowsOptions = { signal?: AbortSignal };

export type UsePusherFetchReturn<T> = {
  isReady: boolean;
  usingPusher: boolean;
  fetchRows: (send: Send<T>, options?: FetchRowsOptions) => Promise<T[]>;
};

const ABORT_ERROR_NAME = 'AbortError';

const createAbortError = () => new DOMException('Aborted', ABORT_ERROR_NAME);

export const isPusherFetchAbort = (error: unknown): boolean =>
  error instanceof DOMException && error.name === ABORT_ERROR_NAME;

export const usePusherFetch = <T>({
  endpoint,
  enabled = true,
  timeout = PUSHER_TIMEOUT,
}: UsePusherFetchOptions): UsePusherFetchReturn<T> => {
  const pusherEnabledState = usePusherEnabledForEndpoint(endpoint);
  const { clientId, hasError, isConnected } = useContext(PusherContext);

  const [hasFallenBack, setHasFallenBack] = useState(false);
  const pendingRef = useRef(new Map<string, Pending<T>>());

  const settle = useCallback((requestId: string): Pending<T> | undefined => {
    const pending = pendingRef.current.get(requestId);

    if (!pending) return undefined;

    clearTimeout(pending.timer);
    pendingRef.current.delete(requestId);

    return pending;
  }, []);

  const abortRequest = useCallback(
    (requestId: string) => settle(requestId)?.reject(createAbortError()),
    [settle]
  );

  const handleMessage = useCallback(
    (payload: unknown) => {
      const requestId = readRequestId(payload);

      if (!requestId) return;

      const pending = settle(requestId);

      if (!pending) return;

      const result = readPusherMessage<T>(payload, requestId);

      if (result.status === 'ok') {
        pending.resolve(result.data);
        return;
      }

      pending.reject(new Error(result.status === 'error' ? result.message : 'Unreadable payload'));
    },
    [settle]
  );

  const shouldUsePusher = enabled && pusherEnabledState === 'on' && !hasFallenBack && !hasError;

  const isSubscribed = usePusherChannel({
    channelName: shouldUsePusher && isConnected && clientId ? `client-${clientId}` : null,
    onEvent: handleMessage,
  });

  const usingPusher = shouldUsePusher && isSubscribed;

  const awaitRows = useCallback(
    (requestId: string) =>
      new Promise<T[]>((resolve, reject) => {
        const timer = setTimeout(() => {
          pendingRef.current.delete(requestId);
          setHasFallenBack(true);
          reject(new Error('Pusher request timeout, falling back to HTTP'));
        }, timeout);

        pendingRef.current.set(requestId, { resolve, reject, timer });
      }),
    [timeout]
  );

  const fetchOverPusher = useCallback(
    async (send: Send<T>, signal?: AbortSignal): Promise<T[]> => {
      const requestId = uuidv4();
      const rows = awaitRows(requestId);
      rows.catch(() => undefined);
      const onAbort = () => abortRequest(requestId);

      signal?.addEventListener('abort', onAbort);

      try {
        await send({ 'x-pusher-client-id': clientId, 'x-pusher-request-id': requestId }, signal);
        return await rows;
      } catch (error) {
        settle(requestId);
        if (signal?.aborted || isPusherFetchAbort(error)) throw error;
        return (await send(undefined, signal)).data;
      } finally {
        signal?.removeEventListener('abort', onAbort);
      }
    },
    [clientId, awaitRows, settle, abortRequest]
  );

  const fetchRows = useCallback(
    async (send: Send<T>, { signal }: FetchRowsOptions = {}): Promise<T[]> => {
      if (signal?.aborted) throw createAbortError();

      try {
        const rows = usingPusher
          ? await fetchOverPusher(send, signal)
          : (await send(undefined, signal)).data;

        if (signal?.aborted) throw createAbortError();

        return rows;
      } catch (error) {
        throw signal?.aborted ? createAbortError() : error;
      }
    },
    [usingPusher, fetchOverPusher]
  );

  useEffect(() => {
    if (enabled) return;

    Array.from(pendingRef.current.keys()).forEach(abortRequest);
  }, [enabled, abortRequest]);

  useEffect(() => {
    const pending = pendingRef.current;

    return () => Array.from(pending.keys()).forEach(abortRequest);
  }, [abortRequest]);

  return {
    isReady: pusherEnabledState !== 'loading' && (!shouldUsePusher || isSubscribed),
    usingPusher,
    fetchRows,
  };
};
